import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from '@tanstack/react-router';
import { isEqual } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { useTabbar } from '@common/providers/useTabbar';
import { Badge } from '@common/ui/Badge';
import { BottomSheet } from '@common/ui/BottomSheet';
import { Button } from '@common/ui/buttons/Button';
import { InputSelect } from '@common/ui/inputs/InputSelect';
import { TextArea } from '@common/ui/inputs/TextArea';
import { PicturesUpload } from '@common/ui/PicturesUpload';
import { useEventEmit } from '@common/utils/events';
import { useRequestSubmit } from '@common/utils/requestSubmitPolyfill';

import { useAccountStore } from '@entities/account';
import { useAuthStore } from '@entities/auth/stores/useAuthStore';
import {
  isStorableUser,
  readStoredUser,
  reportRefusedUser,
} from '@entities/auth/utils';
import { MAX_ABOUT_LENGTH } from '@entities/profile/constants';
import { cities } from '@entities/profile/constants/cities';
import * as dicts from '@entities/profile/dicts';
import {
  UNKNOWN_PHOTO_ERROR_MESSAGE,
  useUploader,
} from '@entities/profile/hooks/useUploader';

import { usePostProfileUpdate } from '@services/hooks/UsersService';

import { SectionCard } from './components';

const formDataSchema = z.object({
  pictures: z
    .array(z.object({ uploadURL: z.string() }))
    .min(1, 'Добавьте хотя бы одну фотографию'),
  about: z
    .string({ message: 'Расскажите о себе' })
    .trim()
    .min(10, 'Не менее 10 символов')
    .max(MAX_ABOUT_LENGTH, `Не более ${MAX_ABOUT_LENGTH} символов`)
    .refine(
      (val) => !/(https?:\/\/[^\s]+)/g.test(val),
      'Нельзя использовать ссылки',
    ),
  city: z
    .string({ message: 'Выберите город' })
    .min(1, 'Поле обязательно для заполнения'),
  goal: z
    .string({ message: 'Выберите цель' })
    .min(1, 'Поле обязательно для заполнения'),
});

const optionalAboutSchema = z
  .string()
  .trim()
  .max(MAX_ABOUT_LENGTH, `Не более ${MAX_ABOUT_LENGTH} символов`)
  .refine((val) => val.length === 0 || val.length >= 10, 'Не менее 10 символов')
  .refine(
    (val) => !/(https?:\/\/[^\s]+)/g.test(val),
    'Нельзя использовать ссылки',
  );

/**
 * A light (matchmaker-only, `dating: false`) account has no about text or
 * goal, so each is optional for it while it is still empty, and an empty
 * one is not sent. Once it has a value it stays required: POST /profile
 * takes both as optional but refuses an empty one (about needs 10 chars,
 * goal a valid value), so the server cannot clear them.
 */
const getFormDataSchema = ({
  isAboutOptional,
  isGoalOptional,
}: {
  isAboutOptional: boolean;
  isGoalOptional: boolean;
}) =>
  formDataSchema.extend({
    about: isAboutOptional ? optionalAboutSchema : formDataSchema.shape.about,
    goal: isGoalOptional ? z.string() : formDataSchema.shape.goal,
  });

type FormData = z.infer<typeof formDataSchema>;

export const Edit: React.FC = () => {
  const tabbar = useTabbar();
  const emit = useEventEmit();
  const formRef = useRef<HTMLFormElement>(null);

  const { user, setUser, verification_status } = useAuthStore();
  const profile = readStoredUser(user);
  const isLightAccount = useAccountStore(
    (state) => state.account?.dating === false,
  );
  const { trigger: triggerPostProfileUpdate } = usePostProfileUpdate();

  const [isOpenEditGoalsModal, setIsOpenEditGoalsModal] = useState(false);
  const [isOpenEditCityModal, setIsOpenEditCityModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Holds off a second save before the disabled button renders.
  const isSavingRef = useRef(false);

  const defaultValues = useMemo(() => {
    return {
      pictures: profile.photos?.map((url) => ({ uploadURL: url })) ?? [],
      about: profile.about,
      city: profile.city,
      goal: profile.goal,
    };
  }, [profile]);

  const {
    control,
    setError,
    reset,
    formState: { errors },
    handleSubmit,
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues,
    resolver: zodResolver(
      getFormDataSchema({
        isAboutOptional: isLightAccount && !profile.about?.trim(),
        isGoalOptional: isLightAccount && !profile.goal,
      }),
    ),
  });

  useEffect(() => {
    reset(defaultValues);
  }, [profile]);

  const watchFields = useWatch({ control });

  const uploader = useUploader({
    onUnknownError: () =>
      setError('pictures', { message: UNKNOWN_PHOTO_ERROR_MESSAGE }),
    onFileTooLarge: ({ message }) =>
      setError('pictures', {
        message,
      }),
  });

  const onSubmit = handleSubmit(async (data) => {
    if (isSavingRef.current) {
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);

    try {
      const updatedUser = await triggerPostProfileUpdate({
        ...(data.about.trim() ? { about: data.about } : {}),
        city: data.city,
        ...(data.goal ? { goal: data.goal } : {}),
        photos: data.pictures.map((picture) => picture.uploadURL || ''),
      });

      if (isStorableUser(updatedUser)) {
        setUser(updatedUser);
        setIsOpenEditGoalsModal(false);
        setIsOpenEditCityModal(false);

        if (data.city !== profile.city) {
          emit('success', {
            title: 'Город изменен',
            description: 'Теперь вы можете лайкать людей из этого города',
          });
        } else {
          emit('info', {
            title: 'Данные обновлены',
            description: 'Они будут видны другим пользователям после модерации',
          });
        }

        return;
      }

      // An answer without a user is a failed save: the stored user stays.
      reportRefusedUser('Edit', updatedUser);
    } catch {
      // A failed request: handled below, as a save without a user is.
    } finally {
      // The screen stays open after a save, so the next one is allowed.
      isSavingRef.current = false;
      setIsSaving(false);
    }

    emit('error', {
      title: 'Данные не загрузились',
      description: 'Попробуйте ещё раз',
    });
  });

  const onReset = () => {
    reset(defaultValues);
  };

  const requestSubmit = useRequestSubmit(formRef);

  useEffect(() => {
    if (!isEqual(watchFields, defaultValues)) {
      tabbar.setBottomSheet(
        <BottomSheet
          buttons={[
            <Button
              testId="profile-edit-save-button"
              disabled={isSaving}
              loading={isSaving}
              onClick={() => requestSubmit()}
            >
              Сохранить
            </Button>,
            <Button
              testId="profile-edit-reset-button"
              variant="actionSecondary"
              onClick={onReset}
            >
              Сбросить
            </Button>,
          ]}
        />,
      );
    } else {
      tabbar.setBottomSheet(undefined);
    }
  }, [watchFields, isSaving, requestSubmit]);

  return (
    <form
      ref={formRef}
      onSubmit={onSubmit}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <SectionCard
        testId="profile-edit-photos"
        title="Фото"
        description="Нажмите на фотографию, чтобы сделать ее главной"
      >
        <Controller
          name="pictures"
          control={control}
          render={({ field }) => (
            <PicturesUpload
              maxNumber={3}
              error={errors.pictures?.message?.toString()}
              value={field.value}
              onChange={(files) => {
                field.onChange(files);
                if (files && files.length > 0) {
                  setError('pictures', { message: '' });
                }
              }}
              upload={uploader}
            />
          )}
        />
      </SectionCard>

      {!profile.is_verified && !verification_status?.has_pending_request && (
        <SectionCard
          testId="profile-edit-verification"
          title="Подтвердите профиль"
          description="Неподтвержденным профилям доступно только 50 свайпов в день"
        >
          <Link
            to="/verification"
            search={{
              gender: profile.sex === 0 ? 'male' : 'female',
              canPass: false,
            }}
          >
            <Button
              testId="profile-edit-verify-button"
              variant="actionSecondary"
            >
              Подтвердить
            </Button>
          </Link>
        </SectionCard>
      )}

      <SectionCard
        testId="profile-edit-about"
        title="Рассказ о себе"
        description="95% пользователей обращает внимание только на анкеты с описанием"
      >
        <Controller
          name="about"
          control={control}
          render={({ field }) => (
            <TextArea
              placeholder="Напишите о себе подробнее"
              description={`${watchFields.about?.length || 0} из ${MAX_ABOUT_LENGTH} символов`}
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              error={errors.about?.message as string}
              maxLength={MAX_ABOUT_LENGTH}
            />
          )}
        />
      </SectionCard>

      <SectionCard
        testId="profile-edit-city"
        title="Город"
        description="При смене города показываем анкеты из нового региона"
      >
        <Controller
          name="city"
          control={control}
          render={({ field }) => (
            <InputSelect
              items={cities.map((city) => ({
                value: city,
                title: city,
              }))}
              value={field.value}
              onSelect={(value) => field.onChange(value)}
              error={errors.city?.message as string}
              isCloseAfterSelect={false}
              isOpenModal={isOpenEditCityModal}
              setIsOpenModal={setIsOpenEditCityModal}
            />
          )}
        />
      </SectionCard>

      <SectionCard
        testId="profile-edit-goal"
        title="Я ищу"
        description="Будьте откровенны — это работает"
      >
        <Controller
          name="goal"
          control={control}
          render={({ field }) => (
            <InputSelect
              items={dicts.goalsDict}
              value={field.value}
              onSelect={(value) => field.onChange(value)}
              error={errors.goal?.message as string}
              isCloseAfterSelect={false}
              isOpenModal={isOpenEditGoalsModal}
              setIsOpenModal={setIsOpenEditGoalsModal}
            />
          )}
        />
      </SectionCard>

      <SectionCard
        testId="profile-edit-short"
        title="В двух словах"
        description="Ответьте на вопросы, которые помогут раскрыть вас"
      >
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[
            profile.zodiac !== undefined &&
              dicts.zodiacSignDict[profile.zodiac],
            profile.height ? profile.height + ' см' : undefined,
            profile.views_on_alcohol !== undefined &&
              dicts.alcoholViewDict[profile.views_on_alcohol],
            profile.sports !== undefined && dicts.sportsDict[profile.sports],
            profile.children !== undefined &&
              dicts.childrenStatusDict[profile.children],
          ]
            .filter(Boolean)
            .map((item, index) => (
              <Badge
                testId="edit-badge-short"
                key={index}
                color="neutralSecondary"
                text={String(item)}
              />
            ))}
        </div>
        <Link to="/profile/edit/short">
          <Button
            testId="profile-edit-short-button"
            variant="actionSecondary"
            style={{ width: '100%' }}
          >
            Ответить на вопросы
          </Button>
        </Link>
      </SectionCard>

      <SectionCard
        testId="profile-edit-interests"
        title="Интересы"
        description="Укажите интересы, чтобы найти людей со схожими увлечениями"
      >
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {profile.interests?.map((interest, index) => (
            <Badge
              testId="edit-badge-interests"
              key={index}
              color="neutralSecondary"
              text={dicts.interestDict[interest]}
            />
          ))}
        </div>
        <Link to="/profile/edit/interests">
          <Button
            testId="profile-edit-interests-button"
            variant="actionSecondary"
            style={{ width: '100%' }}
          >
            Указать интересы
          </Button>
        </Link>
      </SectionCard>

      <SectionCard
        testId="profile-edit-study-and-work"
        title="Учёба и работа"
        description="Расскажите о своей учёбе или текущем месте работы"
      >
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {profile.study_and_work?.map((value, index) => (
            <Badge
              testId="edit-badge-study-and-work"
              key={index}
              color="neutralSecondary"
              text={value}
            />
          ))}
        </div>
        <Link to="/profile/edit/study-and-work">
          <Button
            testId="profile-edit-study-work-button"
            variant="actionSecondary"
            style={{ width: '100%' }}
          >
            Указать учёбу и работу
          </Button>
        </Link>
      </SectionCard>
    </form>
  );
};

Edit.displayName = 'Edit';
